import { html, type TemplateResult } from "lit";
import type { Action, Offer, PublicCard, PublicFaces, PublicRefusal } from "../protocol";
import { optionValue, sentence } from "./game-text";
import { offerAction } from "./targeting";

// 712: a double-faced card's two faces, and which of them each way of playing the card plays.

export type FaceSide = "front" | "back";

export function facesOf(c: PublicCard): PublicFaces | null {
  return optionValue(c.faces);
}

export function faceName(faces: PublicFaces, side: FaceSide): string {
  return side === "front" ? faces.front : faces.back;
}

export function upSide(faces: PublicFaces): FaceSide {
  return faces.back_up ? "back" : "front";
}

export function otherSide(side: FaceSide): FaceSide {
  return side === "front" ? "back" : "front";
}

export const sideLabel = (side: FaceSide): string => (side === "front" ? "Front" : "Back");

// Casting the card or playing it as a land, rather than using it where it is.
export function playsCard(way: Offer[]): boolean {
  const kind = way[0]?.values[0].kind;
  return kind === "CastSpell" || kind === "CastAlternative" || kind === "CastBackFace" || kind === "PlayLand" || kind === "PlayLandBack";
}

// The face an action plays: its back as a land (712.12), cast as its back (712.11b), cast
// converted (702.162) or cast with disturb (702.146a), else the face that's up.
export function sideItPlays(faces: PublicFaces, a: Action): FaceSide {
  if (a.kind === "PlayLandBack" || a.kind === "CastBackFace") return "back";
  if (a.kind === "CastAlternative" && optionValue(faces.converted_by) === a.values[2]) return "back";
  return upSide(faces);
}

export type FacePickerHost = {
  // A face's picture, by its name.
  faceArt: (name: string) => TemplateResult;
  wayLabel: (way: Offer[], face: string) => string;
  useWay: (way: Offer[]) => void;
  // The popup's way out (its Cancel).
  foot: TemplateResult;
};

// Both faces side by side, each lit where a way plays it and dimmed where none does, with the
// engine's reasons, so nothing is played until a face is picked, even when only one can be.
export function renderFacePicker(faces: PublicFaces, ways: Offer[][], refusals: PublicRefusal[], host: FacePickerHost): TemplateResult {
  const face = (side: FaceSide) => renderFace(side, faceName(faces, side), ways.filter((w) => sideItPlays(faces, offerAction(w[0]!)) === side),
    refusals.filter((r) => sideItPlays(faces, r.action) === side), host);
  return html`<div class="pop prompt card-menu face-picker choose-one" role="dialog" aria-label="Choose a face of ${faces.front}">
    <h3>Choose one</h3>
    <div class="sub">Pick the face of ${faces.front} to play.</div>
    <div class="faces">${face("front")}${face("back")}</div>
    ${host.foot}
  </div>`;
}

function renderFace(side: FaceSide, name: string, ways: Offer[][], refusals: PublicRefusal[], host: FacePickerHost): TemplateResult {
  const art = html`${host.faceArt(name)}<span class="face-side">${sideLabel(side)}</span>`;
  if (ways.length === 0) {
    const why = refusals.length > 0 ? [...new Set(refusals.map((r) => sentence(r.reason)))] : ["Can't be played now"];
    return html`<div class="face-option unplayable" aria-disabled="true">
      <div class="face-card">${art}</div>
      <span class="face-name">${name}</span>${why.map((w) => html`<span class="face-how">${w}</span>`)}
    </div>`;
  }
  if (ways.length === 1) {
    const way = ways[0]!;
    const label = host.wayLabel(way, name);
    return html`<div class="face-option playable">
      <button class="face-card" aria-label=${label} @click=${() => host.useWay(way)}>${art}</button>
      <span class="face-name">${name}</span><span class="face-how">${label}</span>
    </div>`;
  }
  return html`<div class="face-option playable">
    <div class="face-card">${art}</div>
    <span class="face-name">${name}</span>
    <div class="choices">${ways.map((w) => html`<button class="btn" @click=${() => host.useWay(w)}>${host.wayLabel(w, name)}</button>`)}</div>
  </div>`;
}
